import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { ProtectedRoute, PublicRoute } from "./components/ProtectedRoute";
import { Layout } from "./components/Layout";
import { Toaster } from "./components/ui/sonner";

// Pages
import LoginPage from "./pages/LoginPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import PublicIntakePage from "./pages/PublicIntakePage";
import ROICalculatorPage from "./pages/ROICalculatorPage";
import DashboardPage from "./pages/DashboardPage";
import SearchResultsPage from "./pages/SearchResultsPage";
import AgenciesPage from "./pages/AgenciesPage";
import AgencyDetailPage from "./pages/AgencyDetailPage";
import UsersPage from "./pages/UsersPage";
import ClientsPage from "./pages/ClientsPage";
import ProjectDetailPage from "./pages/ProjectDetailPage";
import ActiveOrdersPage from "./pages/ActiveOrdersPage";
import ArchivedOrdersPage from "./pages/ArchivedOrdersPage";
import MonthlyActiveClientsPage from "./pages/MonthlyActiveClientsPage";
import ExpiringRenewalsPage from "./pages/ExpiringRenewalsPage";
import NewOrderPage from "./pages/NewOrderPage";
import IntakeFormsPage from "./pages/IntakeFormsPage";
import SettingsPage from "./pages/SettingsPage";
import PendingBillingPage from "./pages/PendingBillingPage";
import ProposalsPage from "./pages/ProposalsPage";
import BulkImportPage from "./pages/BulkImportPage";
import RepDetailPage from "./pages/RepDetailPage";
import MessagesPage from "./pages/MessagesPage";
import UnifiedInboxPage from "./pages/UnifiedInboxPage";
import DirectMessagesPage from "./pages/DirectMessagesPage";
import DirectThreadPage from "./pages/DirectThreadPage";
import ResourcesPage from "./pages/ResourcesPage";
import ROIEstimatorPage from "./pages/ROIEstimatorPage";
import MockupBuilderPage from "./pages/MockupBuilderPage";
import ProspectProposalBuilderPage from "./pages/ProspectProposalBuilderPage";
import SlideLibraryAdminPage from "./pages/SlideLibraryAdminPage";
import SlideLibraryPickerPage from "./pages/SlideLibraryPickerPage";
import ProspectIqHubPage from "./pages/ProspectIqHubPage";
import CompetitorReportsPage from "./pages/CompetitorReportsPage";
import ProspectAuditsPage from "./pages/ProspectAuditsPage";
import ProspectAuditDetailPage from "./pages/ProspectAuditDetailPage";
import CompetitorReportPage, { CompetitorReportBuilderNewPage } from "./pages/CompetitorReportBuilderPage";
import PublicAuditWidgetPage from "./pages/PublicAuditWidgetPage";
import PublicROIAliasPage from "./pages/PublicROIAliasPage";
import PublicProposalSharePage from "./pages/PublicProposalSharePage";

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Public Routes */}
          <Route
            path="/login"
            element={
              <PublicRoute>
                <LoginPage />
              </PublicRoute>
            }
          />

          <Route
            path="/forgot-password"
            element={
              <PublicRoute>
                <ForgotPasswordPage />
              </PublicRoute>
            }
          />

          <Route
            path="/reset-password"
            element={
              <PublicRoute>
                <ResetPasswordPage />
              </PublicRoute>
            }
          />

          {/* Fully public, no-auth intake form for clients */}
          <Route path="/public/intake/:token" element={<PublicIntakePage />} />

          {/* Public ProspectMax ROI calculator (linked from deck QR codes) */}
          <Route path="/roi-calculator" element={<ROICalculatorPage />} />

          {/* Public lead-gen widgets — embeddable on partner sites */}
          <Route path="/tools/audit/:agencyId" element={<PublicAuditWidgetPage />} />
          <Route path="/tools/roi/:agencyId" element={<PublicROIAliasPage />} />

          {/* Public proposal share link — emailed to prospects */}
          <Route path="/public/proposal/:token" element={<PublicProposalSharePage />} />

          {/* Protected Routes */}
          <Route
            path="/search"
            element={
              <ProtectedRoute>
                <Layout>
                  <SearchResultsPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Layout>
                  <DashboardPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/agencies"
            element={
              <ProtectedRoute allowedRoles={["super_admin"]}>
                <Layout>
                  <AgenciesPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/agencies/:agencyId"
            element={
              <ProtectedRoute>
                <Layout>
                  <AgencyDetailPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/users"
            element={
              <ProtectedRoute allowedRoles={["super_admin", "manager", "ops_coordinator"]}>
                <Layout>
                  <UsersPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/users/:userId"
            element={
              <ProtectedRoute allowedRoles={["super_admin", "manager", "ops_coordinator"]}>
                <Layout>
                  <RepDetailPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/clients"
            element={
              <ProtectedRoute allowedRoles={["super_admin", "manager", "rep"]}>
                <Layout>
                  <ClientsPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/orders/active"
            element={
              <ProtectedRoute>
                <Layout>
                  <ActiveOrdersPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/orders/archived"
            element={
              <ProtectedRoute>
                <Layout>
                  <ArchivedOrdersPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/clients/monthly-active"
            element={
              <ProtectedRoute>
                <Layout>
                  <MonthlyActiveClientsPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/renewals"
            element={
              <ProtectedRoute>
                <Layout>
                  <ExpiringRenewalsPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/clients/archived"
            element={<Navigate to="/orders/archived" replace />}
          />

          <Route
            path="/orders/:projectId"
            element={
              <ProtectedRoute>
                <Layout>
                  <ProjectDetailPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* Legacy redirects */}
          <Route path="/projects" element={<Navigate to="/orders/active" replace />} />
          <Route path="/orders" element={<Navigate to="/orders/active" replace />} />

          <Route
            path="/tasks"
            element={<Navigate to="/orders/active" replace />}
          />

          <Route
            path="/orders/new"
            element={
              <ProtectedRoute allowedRoles={["manager", "rep"]}>
                <Layout>
                  <NewOrderPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/intake-forms"
            element={
              <ProtectedRoute>
                <Layout>
                  <IntakeFormsPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/settings"
            element={
              <ProtectedRoute allowedRoles={["super_admin", "manager"]}>
                <Layout>
                  <SettingsPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/billing"
            element={
              <ProtectedRoute allowedRoles={["super_admin", "manager"]}>
                <Layout>
                  <PendingBillingPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/proposals"
            element={
              <ProtectedRoute>
                <Layout>
                  <ProposalsPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/bulk-import"
            element={
              <ProtectedRoute allowedRoles={["super_admin"]}>
                <Layout>
                  <BulkImportPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/messages"
            element={
              <ProtectedRoute>
                <Layout>
                  <UnifiedInboxPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/messages/:clientId"
            element={
              <ProtectedRoute>
                <Layout>
                  <MessagesPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/direct"
            element={
              <ProtectedRoute>
                <Layout>
                  <DirectMessagesPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/direct/:threadId"
            element={
              <ProtectedRoute>
                <Layout>
                  <DirectThreadPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/resources"
            element={
              <ProtectedRoute>
                <Layout>
                  <ResourcesPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/resources/roi-estimator"
            element={
              <ProtectedRoute>
                <Layout>
                  <ROIEstimatorPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/mockups"
            element={
              <ProtectedRoute>
                <Layout>
                  <MockupBuilderPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/prospect-proposals"
            element={
              <ProtectedRoute>
                <Layout>
                  <ProspectProposalBuilderPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/prospect-iq"
            element={
              <ProtectedRoute>
                <Layout>
                  <ProspectIqHubPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/prospect-iq/audits"
            element={<ProtectedRoute><Layout><ProspectAuditsPage /></Layout></ProtectedRoute>}
          />
          <Route
            path="/prospect-iq/audits/:id"
            element={<ProtectedRoute><Layout><ProspectAuditDetailPage /></Layout></ProtectedRoute>}
          />
          <Route
            path="/prospect-iq/competitor-reports"
            element={
              <ProtectedRoute>
                <Layout>
                  <CompetitorReportsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/prospect-iq/competitor-reports/new"
            element={
              <ProtectedRoute>
                <Layout>
                  <CompetitorReportBuilderNewPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/prospect-iq/competitor-reports/:id"
            element={
              <ProtectedRoute>
                <Layout>
                  <CompetitorReportPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/resources/slide-library"
            element={
              <ProtectedRoute>
                <Layout>
                  <SlideLibraryPickerPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/slide-library"
            element={
              <ProtectedRoute allowedRoles={["super_admin"]}>
                <Layout>
                  <SlideLibraryAdminPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          {/* Redirect root to dashboard or login */}
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster />
    </AuthProvider>
  );
}

export default App;
